'use client';

import React from 'react';
import type { RefAttributes } from 'react';
import { XClose } from '@untitledui/icons';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { isLightBackgroundColor } from '../../../utils/color';

const SEMANTIC_NAME_BASE = 'RomaChipBase';

const styles = {
    sizes: {
        xs: {
            root: {
                base: 'h-4.5 px-1.5 text-xs/4 font-medium rounded-xs',
                withLeading: 'pl-0.75',
                withCount: 'pr-0.75',
                withClose: 'pr-0.75',
            },
            content: 'gap-0.75',
            count: 'h-3 px-0.75 text-3xs font-medium',
        },
        sm: {
            root: {
                base: 'h-5 px-1.5 text-sm font-medium rounded-xs',
                withLeading: 'pl-1',
                withCount: 'pr-1',
                withClose: 'pr-1',
            },
            content: 'gap-1',
            count: 'h-3.5 px-1 text-xs font-medium',
        },
        md: {
            root: {
                base: 'h-6 px-2.25 text-md font-medium rounded-xs',
                withLeading: 'pl-1.25',
                withCount: 'pr-0.75',
                withClose: 'pr-1',
            },
            content: 'gap-1.25',
            count: 'h-4 px-1.25 text-xs font-medium',
        },
        lg: {
            root: {
                base: 'h-7 px-2.5 text-display-xs font-medium rounded-sm',
                withLeading: 'pl-1.75',
                withCount: 'pr-1',
                withClose: 'pr-1',
            },
            content: 'gap-1.5',
            count: 'h-5 px-1.5 text-sm font-medium',
        },
    },
    variant: {
        normal: {
            base: 'bg-primary text-secondary ring-1 ring-primary ring-inset',
            count: 'bg-tertiary text-center',
            closeX: 'text-fg-quaternary',
        },
        user: {
            base: 'bg-bg-user-primary text-text-user-primary',
            count: '',
            closeX: '',
        },
        success: {
            base: 'bg-utility-success-100 text-utility-success-700',
            count: '',
            closeX: '',
        },
        warning: {
            base: 'bg-utility-warning-200 text-utility-warning-700',
            count: '',
            closeX: '',
        },
        error: {
            base: 'bg-utility-error-200 text-utility-error-700',
            count: '',
            closeX: '',
        },
        colored_light: {
            base: 'bg-(--chip-bg-color) text-black',
            count: 'text-black/70',
            closeX: 'text-black/50',
        },
        colored_dark: {
            base: 'bg-(--chip-bg-color) text-white',
            count: 'text-white/70',
            closeX: 'text-white/50',
        },
    },
};

export type ChipBaseProps = {
    size?: 'xs' | 'sm' | 'md' | 'lg';
    leading?: React.ReactNode;
    variant?: 'normal' | 'user' | 'success' | 'warning' | 'error';
    color?: string; // Hex or CSS color string. Overrides variant colors.
    dataChipId?: string;
    count?: number;
    isDisabled?: boolean;
    isSelected?: boolean;
    allowsRemoving?: boolean;
    onClose?: () => void;
    className?: string;
    children?: React.ReactNode;
    /** Forwarded to the root <span>. Used by ChipEditable for keyboard focus. */
    tabIndex?: number;
    /** Forwarded to the root <span>. Used by ChipEditable for double-click entry. */
    onDoubleClick?: React.MouseEventHandler<HTMLSpanElement>;
    /** Forwarded to the root <span>. Used by ChipEditable for keyboard entry (useKeyboard). */
    onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
    /** Forwarded to the root <span>. Used by ChipEditable for focus-within tracking (useFocusWithin). */
    onFocus?: React.FocusEventHandler<HTMLElement>;
    /** Forwarded to the root <span>. Used by ChipEditable for focus-within tracking (useFocusWithin). */
    onBlur?: React.FocusEventHandler<HTMLElement>;
};

type ChipCloseButtonProps = AriaButtonProps &
    RefAttributes<HTMLButtonElement> & {
        size?: 'xs' | 'sm' | 'md' | 'lg';
        className?: string;
    };

const closeButtonStyles = {
    xs: { root: 'p-0.5', icon: 'size-2.5' },
    sm: { root: 'p-0.5', icon: 'size-2.5' },
    md: { root: 'p-0.5', icon: 'size-3' },
    lg: { root: 'p-0.75', icon: 'size-3.5' },
};

const ChipCloseButton = ({ size = 'md', className, ...otherProps }: ChipCloseButtonProps) => (
    <AriaButton
        slot="remove"
        aria-label="Remove this chip"
        className={cx(
            'flex cursor-pointer rounded-xxs outline-transparent focus-visible:outline-2 focus-visible:-outline-offset-1 focus-visible:outline-focus-ring disabled:cursor-not-allowed',
            closeButtonStyles[size].root,
            className,
        )}
        {...otherProps}
    >
        <XClose className={cx('transition-inherit-all', closeButtonStyles[size].icon)} strokeWidth="3" />
    </AriaButton>
);

export const ChipBase = React.forwardRef<HTMLSpanElement, ChipBaseProps>((props, ref) => {
    const {
        size = 'sm',
        leading,
        variant = 'normal',
        color,
        dataChipId,
        count,
        isDisabled,
        isSelected,
        allowsRemoving,
        onClose,
        className,
        children,
        tabIndex,
        onDoubleClick,
        onKeyDown,
        onFocus,
        onBlur,
    } = props;

    const isColored = Boolean(color);
    const isColoredLight = isColored ? isLightBackgroundColor(color ?? '') : false;
    const actualVariant = isColored ? (isColoredLight ? 'colored_light' : 'colored_dark') : variant;
    const cssVariables = isColored
        ? ({
              '--chip-bg-color': color,
          } as React.CSSProperties)
        : undefined;

    return (
        <span
            ref={ref}
            data-chip-id={dataChipId}
            tabIndex={tabIndex}
            onDoubleClick={onDoubleClick}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            className={cx(
                SEMANTIC_NAME_BASE,
                'flex cursor-default items-center gap-0.75 bg-primary text-secondary',
                'transition duration-100 ease-linear',
                styles.sizes[size].root.base,
                styles.variant[actualVariant].base,
                // Classes for leading content
                leading && styles.sizes[size].root.withLeading,
                // With X button
                (onClose || allowsRemoving) && styles.sizes[size].root.withClose,
                // With count
                typeof count === 'number' && styles.sizes[size].root.withCount,
                // Disabled
                isDisabled && 'cursor-not-allowed',
                isSelected && 'ring-2 ring-primary_focused',

                className,
            )}
            style={cssVariables}
        >
            <span className={cx('inline-flex min-w-0 items-center gap-1', styles.sizes[size].content)}>
                {leading}

                {children}

                {typeof count === 'number' && (
                    <span
                        className={cx(
                            'flex items-center justify-center rounded-xxs',
                            styles.variant[actualVariant].count,
                            styles.sizes[size].count,
                        )}
                    >
                        {count}
                    </span>
                )}
            </span>

            {(onClose || allowsRemoving) && (
                <ChipCloseButton size={size} onPress={onClose} className={styles.variant[actualVariant].closeX} />
            )}
        </span>
    );
});

ChipBase.displayName = SEMANTIC_NAME_BASE;
